import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { farmAPI } from '@/lib/api';
import { categoryOf } from '@/data/produce';
import { extractErrorMessage } from '@/lib/errors';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ExternalLink, Info, Leaf, Loader2, MapPin, Plus, Sprout } from 'lucide-react';

const STAGES = ['pre_planting', 'planting', 'vegetative', 'flowering', 'fruiting'];

function formatDate(value, language) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(language, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** dd/mm/yyyy, from an ISO date or timestamp (local day). */
function ddmmyyyy(value) {
  if (!value) return '';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(...value.split('-').map((n, i) => (i === 1 ? Number(n) - 1 : Number(n))))
    : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

/**
 * One passage, quoted word for word from the source (the app never
 * words advice itself), with its source directly underneath.
 */
function Recommendation({ rec, retrievedAt, nextUpdate }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="rounded-lg border border-gray-200 p-3" data-testid="nutrition-recommendation">
      {nextUpdate && (
        <p className="mb-2 rounded-md bg-green-50 border border-green-200 px-2 py-1.5 text-xs font-medium text-green-900"
           data-testid="nutrition-advised-by">
          {t('nutrition.live.advisedBy', { date: ddmmyyyy(retrievedAt), next: ddmmyyyy(nextUpdate) })}
        </p>
      )}
      <blockquote className="border-l-4 border-farm-primary pl-3 text-sm text-gray-800 italic" data-testid="nutrition-quote">
        &ldquo;{rec.quote}&rdquo;
      </blockquote>
      <div className="mt-3 border-t border-gray-100 pt-2 text-xs" data-testid="nutrition-source">
        <p className="font-semibold text-farm-primary">
          {t(`nutrition.live.attribution.${rec.attribution_type}`, { org: rec.sources?.[0]?.organisation })}
        </p>
        <ul className="mt-1 space-y-1">
          {(rec.sources || []).map((src) => (
            <li key={src.url}>
              <a
                href={src.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-start gap-1 text-farm-primary underline break-all"
              >
                <ExternalLink className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>{src.title}</span>
              </a>
              <p className="text-muted-foreground">
                {src.organisation}
                {src.published ? ` · ${t('nutrition.live.published', { date: src.published })}` : ''}
                {` · ${t('nutrition.live.retrieved', { date: formatDate(retrievedAt, i18n.language) })}`}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function PlotNutrition({ plot }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [stage, setStage] = useState('vegetative');
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const alive = useRef(true);

  useEffect(() => () => { alive.current = false; }, []);

  // Reads the IITA guidance stored with us for this crop and stage; the
  // request never goes to IITA itself.
  const run = async () => {
    setBusy(true);
    setError('');
    setPlan(null);
    try {
      const { data } = await farmAPI.createNutritionPlan({ user_id: user.id, plot_id: plot.id, growth_stage: stage });
      if (alive.current) setPlan(data);
    } catch (err) {
      if (alive.current) setError(extractErrorMessage(err, t('nutrition.live.unverified')));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  return (
    <Card className="overflow-hidden" data-testid="nutrition-plot">
      <div className="text-white p-4 bg-gradient-to-r from-farm-primary to-green-600">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <MapPin className="w-5 h-5" />
          </div>
          <div>
            <p className="font-bold text-lg">{plot.plot_name}</p>
            <p className="text-white/80 text-sm">
              {plot.crop_type} · {t('farm.plot.hectares', { n: (plot.land_size || 0).toFixed(2) })}
              {plot.location ? ` · ${plot.location}` : ''}
            </p>
          </div>
        </div>
      </div>
      <CardContent className="p-4 space-y-3">
        <label className="block text-sm font-medium text-gray-700" htmlFor={`stage-${plot.id}`}>
          {t('nutrition.live.stageLabel')}
        </label>
        <select
          id={`stage-${plot.id}`}
          value={stage}
          onChange={(e) => setStage(e.target.value)}
          disabled={busy}
          className="w-full h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm"
          data-testid="nutrition-stage"
        >
          {STAGES.map((s) => <option key={s} value={s}>{t(`nutrition.live.stages.${s}`)}</option>)}
        </select>
        <Button
          onClick={run}
          disabled={busy}
          className="w-full bg-farm-primary hover:bg-farm-primary-dark"
          data-testid="nutrition-get"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sprout className="w-4 h-4 mr-2" />}
          {busy ? t('nutrition.live.searching') : t('nutrition.live.get')}
        </Button>

        {error && <p className="text-sm text-red-600" data-testid="nutrition-error">{error}</p>}

        {plan && plan.status !== 'verified' && (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-900" data-testid="nutrition-unverified">
            {t(`nutrition.live.${['unavailable', 'preparing'].includes(plan.status) ? plan.status : 'unverified'}`)}
          </div>
        )}

        {plan && plan.status === 'verified' && (
          <div className="space-y-3" data-testid="nutrition-results">
            {plan.message && <p className="text-sm text-gray-700">{plan.message}</p>}
            {plan.recommendations.map((rec, i) => (
              <Recommendation key={`${rec.sources?.[0]?.url}-${i}`} rec={rec} retrievedAt={plan.retrieved_at}
                              nextUpdate={plan.next_update} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Crop nutrition: for each plot the farmer picks a growth stage and gets
 * recommendations gathered live from IITA, iSDAsoil and the Nigeria
 * Farmers Soil Health Scheme, each with its source shown underneath.
 */
export default function NutritionPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [plots, setPlots] = useState([]);

  useEffect(() => {
    let cancelled = false;
    farmAPI.getPlots(user.id)
      // Crop nutrition only: animals, poultry and fish are left out.
      .then((res) => { if (!cancelled) setPlots((res.data || []).filter((p) => categoryOf(p) === 'crops')); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user.id]);

  return (
    <div className="p-4 animate-fadeIn" data-testid="nutrition-page">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold">{t('nutrition.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('nutrition.subtitleGenerated', { count: plots.length })}</p>
        </div>
      </div>

      <div className="mb-4 flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <p>{t('nutrition.live.disclaimer')}</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-farm-primary" />
        </div>
      ) : plots.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Leaf className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-muted-foreground text-center mb-4">{t('nutrition.noPlots')}</p>
            <Button onClick={() => navigate('/farm')} className="bg-farm-primary hover:bg-farm-primary-dark">
              <Plus className="w-4 h-4 mr-2" /> {t('nutrition.goToFarm')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {plots.map((plot) => <PlotNutrition key={plot.id} plot={plot} />)}
        </div>
      )}
    </div>
  );
}
